<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  ENTRY_ACCESS_KIND_OPTIONS,
  ENTRY_ACCESS_ROLE_OPTIONS,
} from '../utils/imovelShowConstants';
import {
  entryAccessDetailRows,
  entryAccessKindLabel,
  entryAccessSummaryRows,
  formatDateTime,
} from '../utils/imovelShowFormatters';

defineProps({
  auditLabel: {
    type: String,
    default: '',
  },
  accesses: {
    type: Array,
    default: () => [],
  },
  successMessage: {
    type: String,
    default: '',
  },
  permissionDenied: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  empty: {
    type: Boolean,
    default: false,
  },
  deletingId: {
    type: [String, Number],
    default: '',
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  submitLabel: {
    type: String,
    default: '',
  },
  canSubmit: {
    type: Boolean,
    default: false,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  informationHistory: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'edit',
  'remove',
  'clearForm',
  'submit',
  'openLedger',
]);

const entryAccessForm = defineModel('entryAccessForm', {
  type: Object,
  required: true,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Acessos e chaves" density="cozy">
    <div class="flex items-start justify-between gap-3">
      <div class="flex min-w-0 items-start gap-3">
        <span
          class="grid size-9 shrink-0 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
        >
          <span class="i-lucide-key-round size-4" aria-hidden="true" />
        </span>
        <div class="min-w-0">
          <p class="text-[13px] font-semibold text-lais-text">
            {{ auditLabel }}
          </p>
          <p class="mt-1 text-[12px] leading-relaxed text-lais-text-muted">
            Acessos de entrada ficam editáveis aqui; custódia e movimentações
            ficam no ledger canônico de chaves.
          </p>
        </div>
      </div>
      <span
        class="shrink-0 rounded-full bg-lais-bg-soft px-2.5 py-1 text-[11px] font-semibold text-lais-text-muted"
      >
        {{ accesses.length }} acesso(s)
      </span>
    </div>

    <div
      v-if="successMessage"
      class="mt-3 rounded-lais-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] font-semibold text-emerald-800"
    >
      {{ successMessage }}
    </div>
    <div
      v-if="permissionDenied"
      class="mt-3 rounded-lais-sm border border-lais-danger/40 bg-lais-danger-50 px-3 py-2 text-[12px] leading-relaxed text-lais-danger"
    >
      {{ errorMessage }}
    </div>
    <div
      v-else-if="errorMessage"
      class="mt-3 rounded-lais-sm border border-red-200 bg-red-50 px-3 py-2 text-[12px] leading-relaxed text-red-700"
    >
      {{ errorMessage }}
    </div>
    <div
      v-if="loading"
      class="mt-3 flex items-center gap-2 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-3 text-[13px] text-lais-text-body"
      aria-live="polite"
    >
      <span class="i-lucide-loader-2 size-4 animate-spin" aria-hidden="true" />
      Carregando acessos de entrada...
    </div>
    <div
      v-else-if="empty"
      class="mt-3 rounded-lais-sm border border-dashed border-lais-border bg-lais-bg-soft px-3 py-4 text-center text-[13px] text-lais-text-muted"
    >
      Nenhum acesso de entrada cadastrado para este imóvel.
    </div>

    <div v-else class="mt-4 grid gap-3">
      <article
        v-for="entry in accesses"
        :key="entry.id"
        class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-3"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="text-[13px] font-semibold text-lais-text">
              {{ entry.accessLabel || entryAccessKindLabel(entry.kind) }}
            </p>
            <p class="mt-1 text-[12px] text-lais-text-muted">
              {{ entry.helperInfo || 'Sem instrução operacional' }}
            </p>
          </div>
          <LaisStatusBadge
            :label="entry.keyState || 'sem estado'"
            :tone="entry.keysWithAgent ? 'positive' : 'neutral'"
            size="sm"
            dot
          />
        </div>
        <dl class="mt-3 grid gap-2 text-[12px] sm:grid-cols-2">
          <div
            v-for="row in entryAccessSummaryRows(entry)"
            :key="`${entry.id}-${row.label}`"
            class="rounded-lais-sm bg-lais-bg-soft px-3 py-2"
          >
            <dt class="text-lais-text-muted">{{ row.label }}</dt>
            <dd class="mt-0.5 font-semibold text-lais-text">
              {{ row.value || '—' }}
            </dd>
          </div>
        </dl>
        <ul
          v-if="entryAccessDetailRows(entry).length"
          class="mt-3 grid gap-1 text-[12px] text-lais-text-body"
        >
          <li
            v-for="detail in entryAccessDetailRows(entry)"
            :key="`${entry.id}-${detail.key}`"
          >
            <span class="font-semibold text-lais-text">
              {{ detail.label }}:
            </span>
            {{ detail.value }}
          </li>
        </ul>
        <div class="mt-3 flex flex-wrap gap-2">
          <LaisPrimaryButton
            variant="ghost"
            size="sm"
            icon="i-lucide-pencil"
            @click="emit('edit', entry)"
          >
            Editar
          </LaisPrimaryButton>
          <LaisPrimaryButton
            variant="ghost"
            size="sm"
            icon="i-lucide-trash-2"
            :is-loading="deletingId === entry.id"
            :disabled="!!deletingId"
            @click="emit('remove', entry)"
          >
            Excluir acesso
          </LaisPrimaryButton>
        </div>
      </article>
    </div>

    <form
      class="mt-4 grid gap-3 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft p-4"
      @submit.prevent="emit('submit')"
    >
      <div class="flex flex-wrap items-center justify-between gap-2">
        <p class="text-[13px] font-semibold text-lais-text">
          {{
            isEditing ? 'Editar acesso de entrada' : 'Novo acesso de entrada'
          }}
        </p>
        <button
          v-if="isEditing"
          type="button"
          class="text-[12px] font-semibold text-lais-link"
          @click="emit('clearForm')"
        >
          Cancelar edição
        </button>
      </div>
      <div class="grid gap-3 md:grid-cols-2">
        <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
          Tipo de acesso
          <select
            v-model="entryAccessForm.access_kind"
            class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
          >
            <option
              v-for="option in ENTRY_ACCESS_KIND_OPTIONS"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
          Papel
          <select
            v-model="entryAccessForm.role"
            class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
          >
            <option
              v-for="option in ENTRY_ACCESS_ROLE_OPTIONS"
              :key="option.value || 'empty-role'"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
          Responsável
          <input
            v-model="entryAccessForm.assignee_name"
            type="text"
            class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
          />
        </label>
        <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
          Telefone
          <input
            v-model="entryAccessForm.assignee_phone"
            type="tel"
            class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
          />
        </label>
        <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
          E-mail
          <input
            v-model="entryAccessForm.assignee_email"
            type="email"
            class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
          />
        </label>
        <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
          Endereço do responsável
          <input
            v-model="entryAccessForm.metadata.responsible_address"
            type="text"
            class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
          />
        </label>
      </div>
      <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
        Instruções de acesso
        <textarea
          v-model="entryAccessForm.instructions"
          rows="3"
          class="rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-[13px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
        />
      </label>
      <div
        class="flex flex-wrap gap-4 text-[12px] font-semibold text-lais-text"
      >
        <label class="inline-flex items-center gap-2">
          <input
            v-model="entryAccessForm.keys_with_agent"
            type="checkbox"
            class="size-4 rounded border-lais-border text-lais-primary focus:ring-lais-primary"
          />
          Chaves com corretor
        </label>
        <label class="inline-flex items-center gap-2">
          <input
            v-model="entryAccessForm.opt_in"
            type="checkbox"
            class="size-4 rounded border-lais-border text-lais-primary focus:ring-lais-primary"
          />
          Validação ativa
        </label>
      </div>
      <LaisPrimaryButton
        class="w-fit"
        type="submit"
        size="sm"
        icon="i-lucide-save"
        :is-loading="saving"
        :disabled="!canSubmit || saving || permissionDenied"
      >
        {{ submitLabel }}
      </LaisPrimaryButton>
    </form>

    <div
      class="mt-4 rounded-lais-sm border border-lais-border-soft bg-white px-3 py-3"
    >
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p class="text-[13px] font-semibold text-lais-text">
            Histórico e ledger existente
          </p>
          <p class="mt-1 text-[12px] text-lais-text-muted">
            Bloqueios, devoluções, reservas e responsáveis ficam no ledger de
            custódia.
          </p>
        </div>
        <LaisPrimaryButton
          size="sm"
          icon="i-lucide-history"
          @click="emit('openLedger')"
        >
          Abrir ledger
        </LaisPrimaryButton>
      </div>
      <ul
        v-if="informationHistory.length"
        class="mt-3 grid gap-2 text-[12px] text-lais-text-body"
      >
        <li
          v-for="event in informationHistory"
          :key="event.id || event.occurredAt || event.createdAt"
          class="rounded-lais-sm bg-lais-bg-soft px-3 py-2"
        >
          {{
            event.label ||
            event.status ||
            event.eventType ||
            'Evento de entrada'
          }}
          ·
          {{ formatDateTime(event.occurredAt || event.createdAt) }}
        </li>
      </ul>
      <p
        v-else
        class="mt-3 rounded-lais-sm bg-lais-bg-soft px-3 py-2 text-[12px] text-lais-text-muted"
      >
        Sem histórico de visitas retornado; use o ledger para a trilha de
        chaves.
      </p>
    </div>
  </LaisCard>
</template>
